import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { ArrowLeft, Check, HelpCircle, X, Search, Camera, Clock, Download } from 'lucide-react';
import { format } from 'date-fns';
import { API_URL } from '../utils/api';
import { toast } from 'sonner';

const VERDICT_META = {
  'GO': { Icon: Check, accent: 'text-qc-green', bg: 'bg-qc-green/10', border: 'border-qc-green/30', dot: 'bg-qc-green' },
  'MAYBE': { Icon: HelpCircle, accent: 'text-qc-amber', bg: 'bg-qc-amber/10', border: 'border-qc-amber/30', dot: 'bg-qc-amber' },
  'HELL NO': { Icon: X, accent: 'text-qc-red', bg: 'bg-qc-red/10', border: 'border-qc-red/30', dot: 'bg-qc-red' }
};

const filterTabs = [
  { id: 'all', label: 'All' },
  { id: 'GO', label: 'GO' },
  { id: 'MAYBE', label: 'MAYBE' },
  { id: 'HELL NO', label: 'HELL NO' }
];

export default function History() {
  const [history, setHistory] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { filterHistory(); }, [history, activeFilter, search]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API_URL}/scan/history`, { withCredentials: true });
      setHistory(r.data);
    } catch (e) { console.error('Failed to load history'); }
    finally { setLoading(false); }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const response = await axios.get(`${API_URL}/scan/history/export?format=${format}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const ext = format === 'pdf' ? 'pdf' : 'csv';
      const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `greenlight_history_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`History exported as ${format.toUpperCase()}`);
    } catch (e) {
      console.error('Export failed:', e);
      toast.error(e.response?.status === 404 ? 'No scan history to export' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filterHistory = () => {
    let f = [...history];
    if (activeFilter !== 'all') f = f.filter(i => i.decision === activeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(i =>
        (i.item_label && i.item_label.toLowerCase().includes(q)) ||
        i.decision.toLowerCase().includes(q) ||
        (i.reasons && i.reasons.some(r => r.toLowerCase().includes(q)))
      );
    }
    setFiltered(f);
  };

  const formatTimeAgo = (ts) => {
    const now = new Date(); const d = new Date(ts);
    const diff = now - d;
    const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), days = Math.floor(diff / 86400000);
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (days < 7) return `${days}d ago`;
    return format(d, 'MMM d');
  };

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text pb-28"
      data-testid="history-screen"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/home')}
            data-testid="back-button"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-black text-qc-text ml-3 tracking-tight">History</h1>
        </div>
        {history.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              data-testid="export-history-button"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text-dim hover:text-qc-text tappable disabled:opacity-50"
              title="Export history"
            >
              <Download className={`w-5 h-5 ${exporting ? 'animate-pulse' : ''}`} />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-12 bg-qc-surface border border-qc-border rounded-xl shadow-lg py-1 z-50 min-w-[140px]">
                <button
                  onClick={() => handleExport('csv')}
                  data-testid="export-csv-option"
                  className="w-full px-4 py-2 text-left text-sm text-qc-text hover:bg-qc-surface2 flex items-center gap-2"
                >
                  <span className="text-qc-green font-bold">CSV</span>
                  <span className="text-qc-text-dim">Spreadsheet</span>
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  data-testid="export-pdf-option"
                  className="w-full px-4 py-2 text-left text-sm text-qc-text hover:bg-qc-surface2 flex items-center gap-2"
                >
                  <span className="text-qc-red font-bold">PDF</span>
                  <span className="text-qc-text-dim">Printable</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto px-5 pt-2">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-qc-text-faint" />
          <input
            type="text"
            placeholder="Search your scans..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="history-search"
            className="w-full h-12 pl-12 pr-4 bg-qc-surface border border-qc-border rounded-2xl text-qc-text placeholder:text-qc-text-faint focus:outline-none focus:border-qc-green text-base"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              data-testid={`filter-${tab.id}`}
              className={`flex-shrink-0 px-4 h-10 rounded-full text-sm font-bold tracking-wide tappable transition-all ${
                activeFilter === tab.id
                  ? 'bg-qc-green text-qc-bg'
                  : 'bg-qc-surface text-qc-text-dim border border-qc-border hover:border-qc-text-faint'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-qc-green border-t-transparent mx-auto mb-3" />
            <p className="text-qc-text-dim text-sm">Loading history...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-history">
            <div className="w-20 h-20 bg-qc-surface rounded-full flex items-center justify-center mx-auto mb-4 border border-qc-border">
              <Camera className="w-8 h-8 text-qc-text-faint" />
            </div>
            <p className="text-qc-text font-bold mb-2">
              {history.length === 0 ? 'No scans yet' : 'No matching scans'}
            </p>
            <p className="text-qc-text-dim text-sm mb-6">
              {history.length === 0 ? 'Start scanning products to build your history.' : 'Try adjusting your search or filters.'}
            </p>
            {history.length === 0 && (
              <Button
                onClick={() => navigate('/scan')}
                data-testid="scan-first-product"
                className="bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full px-6 h-12 font-extrabold"
              >
                Scan Your First Product
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="history-list">
            {filtered.map((item) => {
              const meta = VERDICT_META[item.decision] || VERDICT_META['MAYBE'];
              const Icon = meta.Icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(`/result/${item.id}`)}
                  data-testid={`history-item-${item.id}`}
                  className="w-full bg-qc-surface border border-qc-border rounded-2xl p-3 flex items-center gap-3 tappable text-left"
                >
                  <div className="relative w-14 h-14 rounded-xl overflow-hidden bg-qc-surface2 flex-shrink-0">
                    {item.thumbnail_url ? (
                      <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-qc-text-faint text-xs">
                        no img
                      </div>
                    )}
                    <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full ${meta.bg} border ${meta.border} flex items-center justify-center`}>
                      <Icon className={`w-3 h-3 ${meta.accent}`} strokeWidth={3} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-qc-text truncate">
                      {item.item_label || 'Older scan'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-black uppercase tracking-wider ${meta.accent}`}>
                        {item.decision}
                      </span>
                      <span className="text-xs text-qc-text-faint">{formatTimeAgo(item.timestamp)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-qc-bg/95 backdrop-blur-lg border-t border-qc-border flex justify-around items-center z-40"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))', paddingTop: '0.5rem' }}
      >
        <button
          onClick={() => navigate('/scan')}
          data-testid="nav-scan"
          className="flex flex-col items-center gap-1 px-6 py-2 text-qc-text-faint hover:text-qc-text-dim"
        >
          <Camera className="w-6 h-6" />
          <span className="text-[11px] font-bold">Scan</span>
        </button>
        <button
          onClick={() => navigate('/history')}
          data-testid="nav-history"
          className="flex flex-col items-center gap-1 px-6 py-2 text-qc-green"
        >
          <Clock className="w-6 h-6" />
          <span className="text-[11px] font-bold">History</span>
        </button>
      </div>
    </div>
  );
}
